import type * as Tone from 'tone';
import type { EffectState, EffectStates } from '../config/effects';
import { EFFECTS, EFFECTS_BY_KEY } from '../config/effects';
import { ensureGraph, isGraphReady, WORKLETS_BLOCKED } from './graph';

/**
 * Effect nodes are created when enabled and disposed when disabled.
 *
 * Keeping all ten permanently wired and "bypassed" with wet = 0 (the previous
 * approach) meant a BitCrusher AudioWorklet, a PitchShift, a Phaser and an
 * AutoWah were processing every sample at all times, whether or not the user
 * had switched any of them on — a constant CPU load that showed up as audio
 * dropouts and UI jank. Rewiring only happens when a checkbox is toggled,
 * never per note or per frame.
 */
const nodes = new Map<string, Tone.ToneAudioNode>();
let wired: string[] = [];

export interface RewireResult {
  /** Effects that could not be created and were switched back off. */
  readonly rejected: readonly string[];
}

export function rewire(states: EffectStates): RewireResult {
  if (!isGraphReady()) return { rejected: [] };
  const g = ensureGraph();
  const rejected: string[] = [];

  g.filter.disconnect();
  for (const key of wired) nodes.get(key)?.disconnect();

  for (const [key, node] of [...nodes]) {
    if (!states[key]?.enabled) {
      try {
        node.dispose();
      } catch {
        /* ignore */
      }
      nodes.delete(key);
    }
  }

  wired = [];
  let tail: Tone.ToneAudioNode = g.filter;

  for (const def of EFFECTS) {
    const state = states[def.key];
    if (!state?.enabled) continue;

    if (def.needsWorklet && WORKLETS_BLOCKED) {
      state.enabled = false;
      rejected.push(def.key);
      continue;
    }

    let node = nodes.get(def.key);
    if (!node) {
      try {
        node = def.build(state);
        nodes.set(def.key, node);
      } catch (err) {
        console.warn(`Efectul „${def.label}” nu a putut fi creat:`, err);
        state.enabled = false;
        rejected.push(def.key);
        continue;
      }
    }
    applyParams(def.key, state);
    tail.connect(node);
    tail = node;
    wired.push(def.key);
  }

  tail.connect(g.delay);
  return { rejected };
}

/** Push a single effect's parameters onto its live node. No-op while it is off. */
export function applyParams(key: string, state: EffectState): void {
  const node = nodes.get(key) as (Tone.ToneAudioNode & Record<string, unknown>) | undefined;
  if (!node) return;
  const def = EFFECTS_BY_KEY[key];
  if (!def) return;

  if (def.hasWet) {
    const wet = node['wet'] as Tone.Param<'normalRange'> | undefined;
    if (wet) wet.value = typeof state.wet === 'number' ? state.wet : 0;
  }
  for (const p of def.params) {
    const value = state[p.key];
    if (typeof value !== 'number') continue;
    if (p.signal) {
      const sig = node[p.key] as { value: number } | undefined;
      if (sig) sig.value = value;
    } else {
      node[p.key] = value;
    }
  }
}

export const activeEffectKeys = (): readonly string[] => wired;
